import { create } from "zustand";
import {
  createId,
  mergeTodoSnapshots,
  normaliseTodoRecord,
  TODO_SCHEMA_VERSION,
  type TodoId,
  type TodoRecord,
  type TodoSnapshot,
} from "./shared/todos";
import {
  createVaultConnectionString,
  hasPersistedVaultState,
  loadPersistedVaultState,
  parseVaultConnectionString,
  savePersistedVaultState,
  type VaultConnectionPayload,
} from "./crypto";

type AuthStatus = "loading" | "setup" | "locked" | "unlocked";

type SyncState = {
  status: "idle" | "connecting" | "connected" | "error";
  peerCount: number;
  lastSyncedAt: number | null;
  lastError: string | null;
};

export type VaultRecord = {
  id: string;
  name: string;
  password: string;
  lastMutatedAt: number;
  todos: Record<TodoId, TodoRecord>;
};

type PersistedTodoState = {
  schemaVersion: typeof TODO_SCHEMA_VERSION;
  deviceId: string;
  activeVaultId: string | null;
  vaults: Record<string, VaultRecord>;
};

type TodoState = PersistedTodoState & {
  authStatus: AuthStatus;
  authError: string | null;
  pin: string | null;
  lastMutationSource: "local" | "remote";
  sync: SyncState;
  hydratePersistedState: () => Promise<void>;
  registerWithPin: (pin: string) => Promise<void>;
  unlockWithPin: (pin: string) => Promise<void>;
  lock: () => void;
  createVault: (name: string, password: string) => string | null;
  connectToVault: (connectionString: string) => Promise<string>;
  setActiveVault: (vaultId: string) => void;
  exportVaultConnectionString: (vaultId: string) => Promise<string>;
  addTodo: (text: string, dueAt?: number | null) => void;
  updateTodo: (id: TodoId, updates: { text?: string; dueAt?: number | null }) => void;
  toggleTodo: (id: TodoId) => void;
  removeTodo: (id: TodoId) => void;
  mergeRemoteSnapshot: (vaultId: string, snapshot: TodoSnapshot) => void;
  setSyncState: (next: Partial<SyncState>) => void;
};

const initialSyncState: SyncState = {
  status: "idle",
  peerCount: 0,
  lastSyncedAt: null,
  lastError: null,
};

type BaseState = Pick<
  TodoState,
  "schemaVersion" | "deviceId" | "activeVaultId" | "vaults" | "authStatus" | "authError" | "pin" | "lastMutationSource" | "sync"
>;

function ensureText(text: string) {
  return text.trim().replace(/\s+/g, " ");
}

function ensurePassword(value: string) {
  return value.trim();
}

function ensurePin(pin: string) {
  return pin.trim();
}

function normaliseDueAt(value: number | null | undefined) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function makeTodo(text: string, origin: string, dueAt: number | null = null): TodoRecord {
  const now = Date.now();
  return {
    id: createId(),
    text,
    completed: false,
    createdAt: now,
    updatedAt: now,
    dueAt: normaliseDueAt(dueAt),
    deletedAt: null,
    origin,
  };
}

function createPersistedState(): PersistedTodoState {
  return {
    schemaVersion: TODO_SCHEMA_VERSION,
    deviceId: createId(),
    activeVaultId: null,
    vaults: {},
  };
}

function normaliseVault(value: unknown): VaultRecord | null {
  if (typeof value !== "object" || value == null) {
    return null;
  }

  const candidate = value as Partial<VaultRecord>;

  if (typeof candidate.id !== "string" || typeof candidate.name !== "string" || typeof candidate.password !== "string") {
    return null;
  }

  if (typeof candidate.todos !== "object" || candidate.todos == null) {
    return null;
  }

  const todoEntries = Object.entries(candidate.todos as Record<string, unknown>)
    .map(([id, todo]) => normaliseTodoRecord(id, todo))
    .filter((todo): todo is TodoRecord => todo != null)
    .map((todo) => [todo.id, todo] as const);

  return {
    id: candidate.id,
    name: candidate.name,
    password: candidate.password,
    lastMutatedAt: typeof candidate.lastMutatedAt === "number" ? candidate.lastMutatedAt : 0,
    todos: Object.fromEntries(todoEntries),
  };
}

function normalisePersistedState(value: unknown): PersistedTodoState {
  const fallback = createPersistedState();

  if (typeof value !== "object" || value == null) {
    return fallback;
  }

  const candidate = value as Partial<PersistedTodoState>;
  const rawVaults = candidate.vaults != null && typeof candidate.vaults === "object" ? Object.values(candidate.vaults) : [];
  const vaultEntries = rawVaults.map((vault) => normaliseVault(vault)).filter((vault): vault is VaultRecord => vault != null);
  const vaults = Object.fromEntries(vaultEntries.map((vault) => [vault.id, vault]));
  const activeVaultId =
    typeof candidate.activeVaultId === "string" && vaults[candidate.activeVaultId] != null
      ? candidate.activeVaultId
      : (vaultEntries[0]?.id ?? null);

  return {
    schemaVersion: TODO_SCHEMA_VERSION,
    deviceId: typeof candidate.deviceId === "string" ? candidate.deviceId : fallback.deviceId,
    activeVaultId,
    vaults,
  };
}

function buildInitialState(): BaseState {
  const persisted = createPersistedState();

  return {
    ...persisted,
    authStatus: "loading",
    authError: null,
    pin: null,
    lastMutationSource: "local",
    sync: {
      ...initialSyncState,
    },
  };
}

function getAuthErrorMessage(error: unknown, fallback: string) {
  if (error instanceof DOMException && error.name === "OperationError") {
    return fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}

function getVault(state: Pick<TodoState, "activeVaultId" | "vaults">) {
  if (state.activeVaultId == null) {
    return null;
  }

  return state.vaults[state.activeVaultId] ?? null;
}

function getPersistedSnapshot(state: TodoState): PersistedTodoState {
  return {
    schemaVersion: TODO_SCHEMA_VERSION,
    deviceId: state.deviceId,
    activeVaultId: state.activeVaultId,
    vaults: state.vaults,
  };
}

function getNextSyncState(current: SyncState, status: SyncState["status"]): SyncState {
  return {
    ...current,
    status,
    lastError: null,
    lastSyncedAt: status === "idle" ? null : current.lastSyncedAt,
  };
}

function createVaultFromShare(payload: VaultConnectionPayload, existing?: VaultRecord): VaultRecord {
  return {
    id: payload.vaultId,
    name: ensureText(payload.name) || existing?.name || "Shared vault",
    password: payload.password,
    lastMutatedAt: existing?.lastMutatedAt ?? 0,
    todos: existing?.todos ?? {},
  };
}

const initialState = buildInitialState();

export const useTodoStore = create<TodoState>()((set, get) => ({
  ...initialState,
  async hydratePersistedState() {
    try {
      const hasState = await hasPersistedVaultState();

      set((state) => {
        if (state.authStatus === "unlocked") {
          return state;
        }

        return {
          ...buildInitialState(),
          authStatus: hasState ? "locked" : "setup",
          authError: null,
        };
      });
    } catch (error) {
      set({
        ...buildInitialState(),
        authStatus: "setup",
        authError: getAuthErrorMessage(error, "TaskGuard could not open this browser's private file system."),
      });
    }
  },
  async registerWithPin(pin) {
    const value = ensurePin(pin);

    if (!/^\d{4,}$/.test(value)) {
      set({
        authError: "Use a PIN with at least 4 digits.",
      });
      return;
    }

    const persisted = createPersistedState();

    try {
      await savePersistedVaultState(persisted, value);
    } catch (error) {
      set({
        authError: getAuthErrorMessage(error, "TaskGuard could not save encrypted data into this browser's private file system."),
      });
      return;
    }

    set({
      ...persisted,
      authStatus: "unlocked",
      authError: null,
      pin: value,
      lastMutationSource: "local",
      sync: {
        ...initialSyncState,
      },
    });
  },
  async unlockWithPin(pin) {
    const value = ensurePin(pin);

    if (!/^\d{4,}$/.test(value)) {
      set({
        authError: "Use the same PIN you used to register this device.",
      });
      return;
    }

    try {
      const decrypted = await loadPersistedVaultState<PersistedTodoState>(value);
      const persisted = normalisePersistedState(decrypted);

      set({
        ...persisted,
        authStatus: "unlocked",
        authError: null,
        pin: value,
        lastMutationSource: "local",
        sync: {
          ...initialSyncState,
        },
      });
    } catch (error) {
      set({
        ...buildInitialState(),
        authStatus: "locked",
        authError: getAuthErrorMessage(error, "That PIN could not unlock your private vault data."),
      });
    }
  },
  lock() {
    set({
      ...buildInitialState(),
      authStatus: "locked",
    });
  },
  createVault(name, password) {
    const nextName = ensureText(name);
    const nextPassword = ensurePassword(password);

    if (nextName.length === 0 || nextPassword.length === 0) {
      return null;
    }

    let createdVaultId: string | null = null;

    set((state) => {
      const vaultId = createId();
      createdVaultId = vaultId;

      return {
        vaults: {
          ...state.vaults,
          [vaultId]: {
            id: vaultId,
            name: nextName,
            password: nextPassword,
            lastMutatedAt: 0,
            todos: {},
          },
        },
        activeVaultId: vaultId,
        lastMutationSource: "local",
        sync: getNextSyncState(state.sync, "connecting"),
      };
    });

    queuePersist();
    return createdVaultId;
  },
  async connectToVault(connectionString) {
    const payload = await parseVaultConnectionString(connectionString);

    set((state) => {
      const existingVault = state.vaults[payload.vaultId];
      const nextVault = createVaultFromShare(payload, existingVault);
      const isSameActiveVault = state.activeVaultId === payload.vaultId && existingVault?.password === payload.password;

      return {
        vaults: {
          ...state.vaults,
          [payload.vaultId]: nextVault,
        },
        activeVaultId: payload.vaultId,
        lastMutationSource: "local",
        sync: isSameActiveVault ? state.sync : getNextSyncState(state.sync, "connecting"),
      };
    });

    queuePersist();
    return payload.vaultId;
  },
  setActiveVault(vaultId) {
    set((state) => {
      if (state.vaults[vaultId] == null || state.activeVaultId === vaultId) {
        return state;
      }

      return {
        activeVaultId: vaultId,
        sync: getNextSyncState(state.sync, "connecting"),
      };
    });

    queuePersist();
  },
  async exportVaultConnectionString(vaultId) {
    const vault = get().vaults[vaultId];

    if (vault == null) {
      throw new Error("Vault not found.");
    }

    return createVaultConnectionString({
      vaultId: vault.id,
      password: vault.password,
      name: vault.name,
    });
  },
  addTodo(text, dueAt) {
    const value = ensureText(text);

    if (value.length === 0) {
      return;
    }

    set((state) => {
      const vault = getVault(state);

      if (vault == null) {
        return state;
      }

      const todo = makeTodo(value, state.deviceId, normaliseDueAt(dueAt));

      return {
        vaults: {
          ...state.vaults,
          [vault.id]: {
            ...vault,
            todos: {
              ...vault.todos,
              [todo.id]: todo,
            },
            lastMutatedAt: todo.updatedAt,
          },
        },
        lastMutationSource: "local",
      };
    });

    queuePersist();
  },
  updateTodo(id, updates) {
    set((state) => {
      const vault = getVault(state);

      if (vault == null) {
        return state;
      }

      const existing = vault.todos[id];

      if (existing == null || existing.deletedAt != null) {
        return state;
      }

      const nextTextValue = updates.text == null ? existing.text : ensureText(updates.text) || existing.text;
      const nextDueAt = updates.dueAt === undefined ? existing.dueAt : normaliseDueAt(updates.dueAt);

      if (nextTextValue === existing.text && nextDueAt === existing.dueAt) {
        return state;
      }

      const now = Date.now();

      return {
        vaults: {
          ...state.vaults,
          [vault.id]: {
            ...vault,
            todos: {
              ...vault.todos,
              [id]: {
                ...existing,
                text: nextTextValue,
                dueAt: nextDueAt,
                updatedAt: now,
                origin: state.deviceId,
              },
            },
            lastMutatedAt: now,
          },
        },
        lastMutationSource: "local",
      };
    });

    queuePersist();
  },
  toggleTodo(id) {
    set((state) => {
      const vault = getVault(state);

      if (vault == null) {
        return state;
      }

      const existing = vault.todos[id];

      if (existing == null || existing.deletedAt != null) {
        return state;
      }

      const now = Date.now();

      return {
        vaults: {
          ...state.vaults,
          [vault.id]: {
            ...vault,
            todos: {
              ...vault.todos,
              [id]: {
                ...existing,
                completed: !existing.completed,
                updatedAt: now,
                origin: state.deviceId,
              },
            },
            lastMutatedAt: now,
          },
        },
        lastMutationSource: "local",
      };
    });

    queuePersist();
  },
  removeTodo(id) {
    set((state) => {
      const vault = getVault(state);

      if (vault == null) {
        return state;
      }

      const existing = vault.todos[id];

      if (existing == null || existing.deletedAt != null) {
        return state;
      }

      const now = Date.now();

      return {
        vaults: {
          ...state.vaults,
          [vault.id]: {
            ...vault,
            todos: {
              ...vault.todos,
              [id]: {
                ...existing,
                deletedAt: now,
                updatedAt: now,
                origin: state.deviceId,
              },
            },
            lastMutatedAt: now,
          },
        },
        lastMutationSource: "local",
      };
    });

    queuePersist();
  },
  mergeRemoteSnapshot(vaultId, snapshot) {
    set((state) => {
      const vault = state.vaults[vaultId];

      if (vault == null) {
        return state;
      }

      const currentSnapshot: TodoSnapshot = {
        schemaVersion: TODO_SCHEMA_VERSION,
        deviceId: state.deviceId,
        lastMutatedAt: vault.lastMutatedAt,
        todos: vault.todos,
      };

      const merged = mergeTodoSnapshots(currentSnapshot, snapshot);

      return {
        vaults: {
          ...state.vaults,
          [vaultId]: {
            ...vault,
            todos: merged.todos,
            lastMutatedAt: merged.lastMutatedAt,
          },
        },
        lastMutationSource: "remote",
        sync: {
          ...state.sync,
          lastSyncedAt: Date.now(),
          lastError: null,
        },
      };
    });

    queuePersist();
  },
  setSyncState(next) {
    set((state) => ({
      sync: {
        ...state.sync,
        ...next,
      },
    }));
  },
}));

let persistTimer: number | undefined;

function queuePersist() {
  if (typeof window === "undefined") {
    return;
  }

  if (persistTimer != null) {
    window.clearTimeout(persistTimer);
  }

  persistTimer = window.setTimeout(() => {
    persistTimer = undefined;
    const state = useTodoStore.getState();

    if (state.authStatus !== "unlocked" || state.pin == null) {
      return;
    }

    void savePersistedVaultState(getPersistedSnapshot(state), state.pin).catch((error) => {
      console.error("Unable to persist encrypted vault state", error);
    });
  }, 120);
}

export function getActiveVaultFromStore() {
  return getVault(useTodoStore.getState());
}

export function getSnapshotFromStore(): TodoSnapshot | null {
  const state = useTodoStore.getState();
  const vault = getVault(state);

  if (vault == null) {
    return null;
  }

  return {
    schemaVersion: TODO_SCHEMA_VERSION,
    deviceId: state.deviceId,
    lastMutatedAt: vault.lastMutatedAt,
    todos: vault.todos,
  };
}
